import { Inject, Injectable } from '@angular/core';
import { map, Observable, of, take } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  AutomationReplacementChipsDataService,
  PagedResponse,
} from '../common/components/automation-replacement-chips/interfaces';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { CRMFieldSchemaApiService, FieldSchemaInterface, FieldType } from '@vendasta/crm';
import { TranslateService } from '@ngx-translate/core';

export interface FieldOverride {
  fieldId: string;
  fieldNameKey?: string;
  hidden?: boolean;
}

interface ExtraData {
  supportedFieldTypes: FieldType[];
  crmObjectType: string;
  crmObjectSubtype: string;
  filterToOnlyStandardFields?: boolean;
  fieldOverrides?: FieldOverride[];
}

const CRMStandardNamespace = 'standard';

@Injectable({ providedIn: 'root' })
export class CrmFieldsSelectorService implements AutomationReplacementChipsDataService<FieldSchemaInterface> {
  supportsSearching = true;

  constructor(
    private readonly crmFieldSchemaApiService: CRMFieldSchemaApiService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
    private readonly translate: TranslateService,
  ) {}

  doLookup(
    searchTerm: string,
    cursor: string,
    pageSize: number,
    extraData: ExtraData,
  ): Observable<PagedResponse<FieldSchemaInterface>> {
    return this.namespace$.pipe(
      switchMap((namespace) =>
        this.crmFieldSchemaApiService.listFieldSchema({
          namespace: namespace,
          crmObjectType: extraData.crmObjectType,
          crmObjectSubtype: extraData.crmObjectSubtype || '',
          filters: {
            namespace: extraData.filterToOnlyStandardFields ? CRMStandardNamespace : '',
          },
          pagingOptions: {
            cursor: cursor,
            pageSize: pageSize,
          },
          searchTerm: searchTerm,
        }),
      ),
      map((resp) => {
        resp.fieldSchemas = (resp?.fieldSchemas || []).filter((x) =>
          extraData.supportedFieldTypes.includes(x.fieldType),
        );
        return resp;
      }),
      map((resp) => {
        const fieldsIdsToHide = extraData?.fieldOverrides?.map((override) =>
          override?.hidden ? override.fieldId : '',
        );
        resp.fieldSchemas = (resp?.fieldSchemas || []).filter(
          (fieldSchema) => !fieldsIdsToHide?.includes(fieldSchema.fieldId) && !fieldSchema.skipAutomation,
        );
        for (const fieldOverride of extraData?.fieldOverrides || []) {
          if (fieldOverride?.fieldNameKey) {
            const field = resp?.fieldSchemas.find((x) => x.fieldId === fieldOverride.fieldId);
            if (field) {
              field.fieldName = this.translate.instant(fieldOverride.fieldNameKey);
            }
          }
        }
        return resp;
      }),
      take(1),
      map((resp) => {
        return {
          results: resp?.fieldSchemas,
          hasMore: resp?.pagingMetadata?.hasMore,
          nextCursor: resp?.pagingMetadata?.nextCursor,
        };
      }),
    );
  }

  convertDataToDisplay(s: FieldSchemaInterface): Observable<string> {
    return of(s?.fieldName || '');
  }

  convertDataToFormValue(x: FieldSchemaInterface): any {
    return x.fieldId;
  }

  getMulti(fieldIds: string[], extraData: ExtraData): Observable<FieldSchemaInterface[]> {
    return this.namespace$.pipe(
      switchMap((namespace) =>
        this.crmFieldSchemaApiService.getMultiFieldSchema({
          namespace: namespace,
          crmObjectType: extraData.crmObjectType,
          crmObjectSubtype: extraData.crmObjectSubtype,
          fieldId: fieldIds,
        }),
      ),
      map((resp) => resp?.fieldSchemas || []),
    );
  }
}
